import React, { FC, useRef, MouseEventHandler, useMemo } from "react";
import classNames from "classnames";

import { Loader } from "components";

import { useButtonEffect } from "./hooks/useButtonEffect";

interface IButton {
  text: string;
  url?: string;
  layout?: "inline" | "block";
  PrefixIcon?: React.ComponentType<any>;
  SuffixIcon?: React.ComponentType<any>;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;
  outline?: boolean;
}

export const Button: FC<IButton> = ({
  url,
  text,
  layout = "inline",
  PrefixIcon,
  SuffixIcon,
  onClick,
  isLoading,
  outline,
}) => {
  const anchorRef = useRef<HTMLAnchorElement>(null);
  let anchorEffect = useButtonEffect(anchorRef);

  const buttonRef = useRef<HTMLButtonElement>(null);
  let buttonEffect = useButtonEffect(buttonRef);

  const renderInner = () => (
    <>
      {PrefixIcon && <PrefixIcon />}
      <span className="button__text">{text}</span>
      {SuffixIcon && <SuffixIcon />}

      {isLoading && (
        <span className="button__loader">
          <Loader size="s" theme="light" />
        </span>
      )}
    </>
  );

  const buttonClasses = useMemo(
    () =>
      classNames("button", [
        `is-${layout}`,
        isLoading && "is-loading",
        outline && "is-outline",
      ]),
    [layout, isLoading, outline]
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={buttonClasses}
        style={buttonEffect}
        ref={buttonRef}
        onClick={onClick}
      >
        {renderInner()}
      </button>
    );
  }

  return (
    <a
      href={url || "#"}
      className={buttonClasses}
      style={anchorEffect}
      ref={anchorRef}
    >
      {PrefixIcon && <PrefixIcon />}
      <span className="button__text">{text}</span>
      {SuffixIcon && <SuffixIcon />}
    </a>
  );
};
